import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { successAlert } from "../../../alert/defaultAlert";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import EmployeeConsumptionForm from "./EmployeeConsumptionForm";
import useEmployeeConsumptionForm from "./useEmployeeConsumptionForm";

/**
 * Lançamento de consumo em modal, usado pelo atalho da tela de Caixas.
 *
 * É só a moldura: campos e envio são os mesmos da página Consumo da equipe > Novo
 * (EmployeeConsumptionForm e useEmployeeConsumptionForm). A recepção lança sem sair do caixa.
 *
 * @param {boolean} show - Controla a abertura do modal.
 * @param {function} setShow - Fecha o modal.
 * @param {function} onSaved - Chamado depois de gravar, para a tela de origem recarregar.
 * @param {object} employee - Opcional: colaborador já escolhido ({ value, label }).
 */
const EmployeeConsumptionModal = ({ show, setShow, onSaved, employee }) => {

    const { t } = useTranslation();
    // Marca a reabertura depois da pergunta de abrir caixa: nesse caso o formulário volta com
    // o que já estava preenchido, em vez de começar limpo.
    const keepValuesOnReopen = useRef(false);

    const { formik, reset, isBranchFixed } = useEmployeeConsumptionForm({
        initialEmployee: employee || null,
        onSaved: () => {
            setShow(false);
            successAlert(t('message.operation_successfully'), () => onSaved?.());
        },
        // O alerta do SweetAlert fica atrás do modal do Bootstrap, então o modal sai da frente
        // durante a pergunta e volta se a pessoa não abrir o caixa.
        onCashPromptOpen: () => setShow(false),
        onCashPromptDeclined: () => {
            keepValuesOnReopen.current = true;
            setShow(true);
        }
    });

    // Cada abertura começa limpa, para o próximo lançamento não herdar o colaborador anterior.
    useEffect(() => {
        if (!show) {
            return;
        }
        if (keepValuesOnReopen.current) {
            keepValuesOnReopen.current = false;
            return;
        }
        reset();
    }, [show, reset]);

    return (
        <ModalPageEvent
            show={show}
            setShow={setShow}
            centered={true}
            // lg para os botões de preço e de acerto caberem lado a lado com os rótulos inteiros.
            size="lg"
            title={t('cash_flow.new_employee_consumption')}
            btnText={t('save')}
            btnOnClick={formik.submitForm}
            btnDisabled={formik.isSubmitting}
            btnCloseOnClick={() => setShow(false)}
            child={<EmployeeConsumptionForm formik={formik} isBranchFixed={isBranchFixed} />}
        />
    );
};

export default EmployeeConsumptionModal;
